import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartDataPoint, ChartView } from '@/hooks/useChartData'
import { PlanData } from '@/hooks/useStudioAnalytics'
import { useChartAxisTheme } from '@/hooks/useThemeColors'
import { formatCurrency } from '@/lib/utils'

export interface RevenueChartProps {
  chartData: ChartDataPoint[]
  chartView: ChartView
  planData: PlanData | null
  visibleLines: Record<string, boolean>
  currency?: string
}

export function RevenueChart({
  chartData,
  chartView,
  planData,
  visibleLines,
  currency = 'RUB'
}: RevenueChartProps) {
  // Сетка и оси recharts красятся SVG-атрибутами: CSS-переменная там не
  // разрешается, поэтому цвет темы приходится вычислять в JS.
  const chartTheme = useChartAxisTheme()

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid {...chartTheme.grid} />
          <XAxis dataKey="date" {...chartTheme.axis} />
          <YAxis {...chartTheme.axis} tickFormatter={(value) => formatCurrency(Number(value), currency)} />
          <Tooltip
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                const date = payload[0].payload.date
                return chartView === 'cumulative' ? `${date} (накопительно)` : date
              }
              return label
            }}
            content={(props: any) => {
              if (!props.active || !props.payload) return null
              
              // Фильтруем только неплановые линии
              const filteredPayload = props.payload.filter((item: any) => 
                !item.dataKey?.startsWith('plan')
              )
              
              if (filteredPayload.length === 0) return null
              
              const labels: Record<string, string> = 
                chartView === 'daily' 
                  ? {
                      revenue: 'Выручка всего',
                      newRevenue: 'Выручка по новым',
                      existingRevenue: 'Выручка по не новым'
                    }
                  : {
                      cumRevenue: 'Выручка всего',
                      cumNewRevenue: 'Выручка по новым',
                      cumExistingRevenue: 'Выручка по не новым'
                    }
              
              return (
                <div className="bg-card text-foreground p-2 border border-border rounded shadow-sm">
                  <p className="text-sm font-medium mb-1">{props.label}</p>
                  {filteredPayload.map((item: any, index: number) => {
                    const payload = props.payload[0].payload
                    const label = labels[item.dataKey] || item.dataKey
                    let displayValue = formatCurrency(Number(item.value), currency)
                    
                    // Добавляем план если в накопительном режиме
                    if (chartView === 'cumulative' && item.dataKey?.startsWith('cum') && planData) {
                      let planValue = null
                      if (item.dataKey === 'cumRevenue') planValue = payload.planRevenue
                      else if (item.dataKey === 'cumNewRevenue') planValue = payload.planNewRevenue
                      else if (item.dataKey === 'cumExistingRevenue') planValue = payload.planExistingRevenue
                      
                      if (planValue !== null && planValue !== undefined) {
                        displayValue = `${formatCurrency(Number(item.value), currency)} / ${formatCurrency(Number(planValue), currency)}`
                      }
                    }
                    
                    return (
                      <p key={index} className="text-xs" style={{ color: item.color }}>
                        {label}: {displayValue}
                      </p>
                    )
                  })}
                </div>
              )
            }}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'revenue' : 'cumRevenue'}
            name={chartView === 'daily' ? 'revenue' : 'cumRevenue'}
            stroke="#4CAF50"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.revenue ? 1 : 0.2}
            strokeDasharray={visibleLines.revenue ? '0' : '5 5'}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'newRevenue' : 'cumNewRevenue'}
            name={chartView === 'daily' ? 'newRevenue' : 'cumNewRevenue'}
            stroke="#2196F3"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.newRevenue ? 1 : 0.2}
            strokeDasharray={visibleLines.newRevenue ? '0' : '5 5'}
          />
          <Line
            type="monotone"
            dataKey={chartView === 'daily' ? 'existingRevenue' : 'cumExistingRevenue'}
            name={chartView === 'daily' ? 'existingRevenue' : 'cumExistingRevenue'}
            stroke="#FF9800"
            strokeWidth={2}
            dot={{ r: 3 }}
            opacity={visibleLines.existingRevenue ? 1 : 0.2}
            strokeDasharray={visibleLines.existingRevenue ? '0' : '5 5'}
          />
          {/* Plan lines (только в накопительном режиме) */}
          {chartView === 'cumulative' && planData && (
            <>
              <Line
                type="monotone"
                dataKey="planRevenue"
                stroke="#4CAF50"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.revenue ? 0.5 : 0.1}
              />
              <Line
                type="monotone"
                dataKey="planNewRevenue"
                stroke="#2196F3"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.newRevenue ? 0.5 : 0.1}
              />
              <Line
                type="monotone"
                dataKey="planExistingRevenue"
                stroke="#FF9800"
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                strokeDasharray="5 5"
                opacity={visibleLines.existingRevenue ? 0.5 : 0.1}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
